import React, { useEffect, useRef, useState } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Button,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useBOMDashboardContext } from "../../BOMDashboard.context";
import { BootstrapInput } from "../../../../../../../../components/widgets/BootstrapInput";
import SharePointIcon from "../../../../../../../../components/CustomIcons/SharePointIcon";
import DrawingsSource from "./DrawingsSource";
import { handleSelectFile } from "./handleSelectFile";
import { fetchDbDrawings } from "./fetchDbDrawings";
import { useMcsContext } from "../../../MCS.context";
import { getHashdata } from "../../../../../../../../components/Tabs/Utils/hashFunctions";

const initialState = {
  projectName: "",
  dwgNumber: "",
  revision: "",
  sheet: "",
  title: "",
};
const DrawingSelection = ({
  selectedDrawings,
  handleAddingNewDrawing,
  setSelectedDrawings,
}) => {
  const { plant, isPlant } = useMcsContext();
  const { project } = useBOMDashboardContext();

  const [source, setSource] = useState(isPlant ? "database" : "sharepoint");
  const [dbData, setDbData] = useState([]);
  const [spData, setSpData] = useState([]);

  const [data, setData] = useState([]);
  const [selectedDrawing, setSelectedDrawing] = useState(initialState);

  const [revisions, setRevisions] = useState([]);
  const [sheets, setSheets] = useState([]);

  const [selectionResult, setSelectionResult] = useState({});

  const [loading, setLoading] = React.useState(false);
  const [engData, setEngData] = useState();
  const [feedback, setFeedback] = useState();

  // DrawingBomContext already fetches+adds the drawing named by the
  // "drawing-id" hash key to the working set below; this mirrors the same
  // deep link into the Sub-Project/Drawing/Revision/Sheet selects above so
  // they reflect what was actually opened instead of sitting empty.
  const deepLinkAppliedRef = useRef(false);

  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (!plant?.id || !Array.isArray(plant?.projects) || !plant.projects.length)
      return;

    const drawingId = getHashdata(window.location.hash, "drawing-id");
    if (!drawingId) return;

    let matchedProject = null;
    let matchedDrawing = null;
    for (const proj of plant.projects) {
      const found = (proj.drawings || []).find((d) => d.id === drawingId);
      if (found) {
        matchedProject = proj;
        matchedDrawing = found;
        break;
      }
    }

    if (!matchedProject || !matchedDrawing) return;

    deepLinkAppliedRef.current = true;

    const projectDrawings = matchedProject.drawings || [];
    setDbData(projectDrawings);
    setData(projectDrawings);

    setSelectedDrawing({
      projectName: matchedProject.name,
      dwgNumber: matchedDrawing.dwgNumber || "",
      revision: matchedDrawing.revision || "",
      sheet: matchedDrawing.sheetNumber || "",
      title: matchedDrawing.title || "",
    });

    setRevisions([
      ...new Set(
        projectDrawings
          .filter(
            (d) =>
              d.dwgNumber === matchedDrawing.dwgNumber && d.revision !== "",
          )
          .map((d) => d.revision),
      ),
    ]);

    setSheets([
      ...new Set(
        projectDrawings
          .filter(
            (d) =>
              d.dwgNumber === matchedDrawing.dwgNumber &&
              d.revision === matchedDrawing.revision &&
              d.sheetNumber !== "",
          )
          .map((d) => d.sheetNumber),
      ),
    ]);

    setSelectionResult(matchedDrawing);
  }, [plant?.id, plant?.projects]);

  useEffect(() => {
    const fetchDb = async () => {
      const res = await fetchDbDrawings({ project });
      setDbData(res);
    };
    !isPlant && fetchDb();
    // onSelectFromSharepoint();
  }, []);

  useEffect(() => {
    setSelectedDrawing(initialState);

    if (source === "sharepoint") {
      setData(spData);
    } else {
      const fetchDb = async () => {
        const res = await fetchDbDrawings({ project });
        setDbData(res);
        setData(res);
      };
      fetchDb();
    }
  }, [source]);

  const onDrawingChange = (drawing) => {
    setSelectionResult(drawing);
  };

  const handleProjectChange = (event) => {
    const projectName = event.target.value;

    setSelectedDrawing({ ...initialState, projectName });
    const selectedProject = plant.projects.find((p) => p.name === projectName);

    console.log("selected Project :", selectedProject);
    // add drawings to data based on selected project

    // const showingDrawing = (selectedProject.drawings || []).filter((d) =>
    //   ["pnid", "estimation", "engg"].includes(d.lifecycle?.toLowerCase()),
    // );
    // setDbData(showingDrawing);
    // setData(showingDrawing);

    setDbData(selectedProject.drawings);
    setData(selectedProject.drawings);
  };

  const handleNameChange = (event) => {
    const dwgNumber = event.target.value;
    setSelectedDrawing({
      ...selectedDrawing,
      dwgNumber,
      revision: "",
      sheet: "",
      title: "",
    });

    const revs = [
      ...new Set(
        data
          .filter((d) => d.dwgNumber === dwgNumber && d.revision !== "")
          .map((d) => d.revision),
      ),
    ];
    setRevisions(revs);
    setSheets([]);

    // if no revisions, fetch drawing
    if (!revs.length) {
      const selectedDrawing = data.find((d) => d.dwgNumber === dwgNumber);
      onDrawingChange(selectedDrawing);
    }
  };

  const handleRevisionChange = (event) => {
    const revision = event.target.value;

    setSelectedDrawing({ ...selectedDrawing, revision, sheet: "" });

    const sheetList = [
      ...new Set(
        data
          .filter(
            (d) =>
              d.dwgNumber === selectedDrawing.dwgNumber &&
              d.revision === revision &&
              d.sheetNumber !== "",
          )
          .map((d) => d.sheetNumber),
      ),
    ];
    setSheets(sheetList);

    // if no sheets, fetch drawing
    if (!sheetList.length) {
      const selectedDrawing = data.find(
        (d) =>
          d.dwgNumber === selectedDrawing.dwgNumber && d.revision === revision,
      );
      onDrawingChange(selectedDrawing);
    }
  };

  const handleSheetChange = (event) => {
    const sheetNumber = event.target.value;

    setSelectedDrawing({ ...selectedDrawing, sheet: sheetNumber });
    const selectedDrawingData = data.find(
      (d) =>
        d.dwgNumber === selectedDrawing.dwgNumber &&
        d.revision === selectedDrawing.revision &&
        d.sheetNumber === sheetNumber,
    );
    console.log({ data, selectedDrawingData });
    onDrawingChange(selectedDrawingData);
  };

  const onAddSelectedDrawing = async () => {
    // if (source === "database") {
    if (!selectionResult.id) {
      alert("Please select a specific drawing to be added.");
    } else {
      const isSelectedBefore = selectedDrawings.find(
        (d) => d.id === selectionResult,
      );

      // if (!isSelectedBefore?.id) {
      handleAddingNewDrawing({ drawing: selectionResult });
      // } else {
      //   alert("This drawing is already selected !");
      // }
    }
    // } else {
    //   // Handle add if new drawing to db
    //   const randomId = 23;
    //   console.log(spData[0]);
    //   setSelectedDrawings([
    //     ...selectedDrawings,
    //     { ...spData[0], title: "this is from sp" },
    //   ]);
    // }
  };

  const onSelectFile = async (e) => {
    setData([]);
    setSelectedDrawing(initialState);

    await handleSelectFile({ e, dbData, setData });
  };

  return (
    <Stack direction={"row"}>
      {!plant?.id && (
        <>
          <DrawingsSource source={source} setSource={setSource} />

          {source === "sharepoint" && (
            <Button
              variant="outlined"
              component="label"
              sx={{ mt: 3, mr: 1, mb: 2, transform: "translateY(-2px)" }}
              startIcon={<SharePointIcon size={23} />}
              // onClick={onSelectFromSharepoint}
            >
              Browse
              <input
                // id="upload-button"
                type="file"
                hidden
                onChange={onSelectFile}
              />
            </Button>
          )}
        </>
      )}

      {plant?.id && (
        <FormControl
          variant="standard"
          size="small"
          sx={{
            m: 1,
            ml: 0,
            mt: 0,
            minWidth: 180,
          }}
          margin="normal"
          disabled={!plant?.projects?.[0]}
        >
          <InputLabel id="mrq-label" shrink sx={{ fontSize: "1.1rem" }}>
            Project/WBS #
          </InputLabel>
          <Select
            value={selectedDrawing.projectName || ""}
            onChange={handleProjectChange}
            input={<BootstrapInput />}
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: 1,
            }}
          >
            {Array.from(new Set(plant?.projects))?.map((project) => (
              <MenuItem key={project.id} value={project.name}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl
        variant="standard"
        size="small"
        sx={{ m: 1, ml: 0, mt: 0, minWidth: 150 }}
        margin="normal"
        disabled={!data?.length}
      >
        <InputLabel id="mrq-label" shrink sx={{ fontSize: "1.1rem" }}>
          Drawing/Doc #
        </InputLabel>
        <Select
          value={selectedDrawing.dwgNumber}
          onChange={handleNameChange}
          input={<BootstrapInput />}
        >
          {Array.from(new Set(data?.map((d) => d.dwgNumber))).map(
            (dwgNumber) => (
              <MenuItem key={dwgNumber} value={dwgNumber}>
                {dwgNumber}
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>

      <FormControl
        variant="standard"
        size="small"
        sx={{ m: 1, ml: 0, mt: 0, minWidth: 60 }}
        margin="normal"
        disabled={!selectedDrawing.dwgNumber}
      >
        <InputLabel id="mrq-label" shrink sx={{ fontSize: "1.1rem" }}>
          Revision
        </InputLabel>
        <Select
          value={selectedDrawing.revision}
          onChange={handleRevisionChange}
          input={<BootstrapInput />}
        >
          {revisions
            ?.sort((a, b) => a - b)
            ?.map((rev) => (
              <MenuItem key={rev} value={rev}>
                {rev}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <FormControl
        variant="standard"
        size="small"
        sx={{ m: 1, ml: 0, mt: 0, minWidth: 120 }}
        margin="normal"
        disabled={!selectedDrawing.revision}
      >
        <InputLabel id="mrq-label" shrink sx={{ fontSize: "1.1rem" }}>
          Sheet
        </InputLabel>
        <Select
          value={selectedDrawing.sheet}
          onChange={handleSheetChange}
          input={<BootstrapInput />}
        >
          {sheets?.map((sheet) => (
            <MenuItem key={sheet} value={sheet}>
              {sheet}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        variant="standard"
        size="small"
        sx={{ m: 1, ml: 0, mt: 0, minWidth: 180 }}
        margin="normal"
        disabled={!selectedDrawing.revision}
      >
        <InputLabel id="mrq-label" shrink sx={{ fontSize: "1.1rem" }}>
          Title
        </InputLabel>
        <BootstrapInput disabled value={selectionResult?.title} />
      </FormControl>

      {/* <IconButton component="label">
        <SharePointIcon size={23} />
        <input
          // id="upload-button"
          type="file"
          hidden
          onChange={(e) => console.log(e.target.files)}
        />
      </IconButton> */}

      <Button
        variant="contained"
        sx={{ mt: 3, ml: 1, mb: 2 }}
        startIcon={<AddCircleOutlineIcon />}
        onClick={onAddSelectedDrawing}
      >
        Add
      </Button>
    </Stack>
  );
};

export default DrawingSelection;
